import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';

import { AppConfigService, ClientConfigStore, PublicConfig } from '@shared/data';
import { OAuthService } from 'angular-oauth2-oidc';
import { authConfig } from './oauth-config';
import { Roles } from '@shared/util-auth/models/roles';
import { Base64 } from 'js-base64';
import { ClientLogRequestDto, ClientlogService } from 'lion-api';

/**
 * An interface representing a sub set of the identity claims
 * in the OIDC ID token.
 */
interface IdentityClaims {
  readonly name: string;
  readonly preferred_username: string;
}

/**
 * An authentication and authorization service which provides
 * convenient methods for validating whether a user is authenticated
 * and authorized by certain roles.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  roles: string[] = [];
  dzbbvId = '';
  zgpnrs: string[] = [];
  name = '';
  username = '';
  gruppe = '';

  authenticated = signal(false);

  private oauthService = inject(OAuthService);
  private router = inject(Router);
  private appConfigService = inject(AppConfigService);
  private clientConfigStore = inject(ClientConfigStore);
  private clientlogService = inject(ClientlogService);

  constructor() {
    this.oauthService.events.subscribe({
      next: (event) => {
        if (
          event.type === 'token_refresh_error' ||
          event.type === 'silent_refresh_error' ||
          event.type === 'silent_refresh_timeout' ||
          event.type === 'session_error'
        ) {
          console.log(`Logging out: EventType: ${event.type}.`);
          this.logout();
        }

        if (event.type === 'token_expires') {
          this.oauthService.refreshToken().then();
        }
      }
    });

    effect(() => {
      const publicConfig = this.appConfigService.publicConfig();
      untracked(() => {
        if (publicConfig) {
          this.initializeOAuthServiceAndLogin(publicConfig).then(() => {
            const authenticated = this.oauthService.hasValidAccessToken();
            this.authenticated.update(() => authenticated);
            if (authenticated) {
              this.setAuthData();
            }
          });
        }
      });
    });
  }

  isMatchedRole(...roles: string[]) {
    if (this.roles && this.roles.length > 0) {
      for (const role of roles) {
        if (this.roles.includes(role)) {
          return true;
        }
      }
    }
    return roles.includes(Roles.PERMIT_ALL);
  }

  hasRoles(...roles: string[]): boolean {
    if (!this.authenticated()) {
      return false;
    }
    return this.isMatchedRole(...roles);
  }

  logout(redirectToLogoutPage = true) {
    if (!redirectToLogoutPage) {
      this.oauthService.postLogoutRedirectUri = window.location.origin;
    }

    const idToken = sessionStorage.getItem('id_token') || this.oauthService.getIdToken();
    const logoutParams: Record<string, string> = {
      client_id: this.oauthService.clientId || ''
    };
    if (idToken) {
      logoutParams['id_token_hint'] = idToken;
    }

    this.oauthService.revokeTokenAndLogout(logoutParams).then(() => {
      if (redirectToLogoutPage && this.clientConfigStore.isLicoOrigin()) {
        this.router.navigate(['/logout']);
      } else {
        this.router.navigate(['/']);
      }
    });
  }

  private setAuthData() {
    const accessToken: string = this.oauthService.getAccessToken();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const token: any = JSON.parse(Base64.decode(accessToken.split('.')[1]));
    const clientId = this.clientConfigStore.clientId();

    if (token.resource_access && token.resource_access[clientId]) {
      this.roles = token.resource_access[clientId].roles;
    }

    if (this.clientConfigStore.isLicoOrigin()) {
      this.zgpnrs = token.atruvia.user_acts_for_organisation_zgpnrs;
    } else {
      if (token.dzbank_org_data) {
        this.dzbbvId = token.dzbank_org_data.dzbbv_id;
        this.zgpnrs = token.dzbank_org_data.zgpnrs;
      }
      this.gruppe = token.dzbank_user_data?.gruppe || '';
    }

    const claims = this.oauthService.getIdentityClaims() as IdentityClaims;
    this.name = claims.name;
    this.username = claims.preferred_username;

    if (this.clientConfigStore.isLicoOrigin()) {
      const authorizationError = token.atruvia?.authorization_error;

      if (authorizationError) {
        this.logErrorToBackend(
          `LiCo-Authorization error for user ${this.username || 'unknown'}: ${authorizationError}`,
          'Authorization error successfully logged to backend',
          'Failed to log authorization error to backend'
        );
        return;
      }

      const rolesList = token.resource_access?.lico?.roles || [];
      const hasLicoRoles = rolesList.length > 0;
      if (!hasLicoRoles) {
        this.logErrorToBackend(
          `LiCo-Authorization error: User ${this.username || 'unknown'} has no lico roles (roles list is empty).`,
          'Missing roles logged to backend',
          'Failed to log missing roles'
        );
      }

      const hasZgpnrs = this.zgpnrs && this.zgpnrs.length > 0;
      if (!hasZgpnrs) {
        this.logErrorToBackend(
          `LiCo-Authorization error: User ${this.username || 'unknown'} has no associated ZGPNRs (user_acts_for_organisation_zgpnrs list is empty).`,
          'Missing ZGPNRs logged to backend',
          'Failed to log missing ZGPNRs'
        );
      }
    } else {
      const rolesList = token.resource_access?.lion?.roles || [];
      const hasLionRoles = rolesList.length > 0;
      if (!hasLionRoles) {
        this.logErrorToBackend(
          `LiOn-Authorization error: User ${this.username || 'unknown'} has no lion roles (roles list is empty).`,
          'Missing lion roles logged to backend',
          'Failed to log missing lion roles'
        );
      }

      const hasZgpnrs = this.zgpnrs && this.zgpnrs.length > 0;
      if (!hasZgpnrs) {
        this.logErrorToBackend(
          `LiOn-Authorization error: User ${this.username || 'unknown'} has no associated ZGPNRs (zgpnrs list is empty).`,
          'Missing ZGPNRs logged to backend',
          'Failed to log missing ZGPNRs'
        );
      }
    }
  }

  private logErrorToBackend(message: string, successLog: string, errorLog: string): void {
    const logDto: ClientLogRequestDto = {
      timestamp: new Date().toISOString(),
      loggerName: 'AuthService',
      logSeverity: 'ERROR',
      message
    };
    this.clientlogService.clientlogPost([logDto]).subscribe({
      next: () => console.log(successLog),
      error: (err) => console.error(errorLog, err)
    });
  }

  private async initializeOAuthServiceAndLogin(config: PublicConfig) {
    if (this.clientConfigStore.isLicoOrigin()) {
      authConfig.clientId = 'lico';
    } else {
      authConfig.clientId = 'lion';
    }
    this.oauthService.configure(authConfig);

    this.oauthService.issuer = config?.message.authorizationServerUrl1;
    if (this.clientConfigStore.isLicoOrigin()) {
      this.oauthService.issuer = config?.message.authorizationServerUrl2;
    }

    if (this.clientConfigStore.isLicoOrigin()) {
      this.oauthService.postLogoutRedirectUri = window.location.origin + '/logout';
    } else {
      this.oauthService.postLogoutRedirectUri = window.location.origin;
    }

    await this.oauthService.loadDiscoveryDocumentAndTryLogin();
    this.router.initialNavigation();
    this.oauthService.setupAutomaticSilentRefresh();

    if (window.location.pathname.startsWith('/logout')) {
      return;
    }

    if (!this.oauthService.hasValidIdToken() || !this.oauthService.hasValidAccessToken()) {
      this.oauthService.initCodeFlow();
    }
  }
}
